import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { eq } from 'ember-truth-helpers';
import type Owner from '@ember/owner';
import type { Store } from '@warp-drive/core';
import type Session from '../services/session';
import type Notifications from '../services/notifications';
import type { UserItem } from '../schemas/user-item.ts';
import type { Tag } from '../schemas/tag.ts';
import {
  getUserItems,
  updateUserItemStatus,
  getTags,
} from '../builders/profile';
import TagPicker from './tag-picker.gts';
import Icon from './icon';
import tw from '../helpers/tw';
import CheckIcon from '~icons/lucide/check';
import XIcon from '~icons/lucide/x';
import UndoIcon from '~icons/lucide/undo-2';

interface ItemActionsSignature {
  Args: {
    /** The catalog item ID to look up in user's profile */
    itemId: string | null;
  };
}

export default class ItemActions extends Component<ItemActionsSignature> {
  @service declare store: Store;
  @service declare session: Session;
  @service declare notifications: Notifications;

  @tracked userItem: UserItem | null = null;
  @tracked tags: Tag[] = [];
  @tracked isLoading = true;
  @tracked actionInProgress = false;

  constructor(owner: Owner, args: { itemId: string | null }) {
    super(owner, args);
    if (this.session.isAuthenticated) {
      void this.loadUserItem();
    } else {
      this.isLoading = false;
    }
  }

  async loadUserItem() {
    try {
      // Fetch user items filtered to this specific catalog item
      // The API returns user-items that embed the item data
      const [itemsResult, tagsResult] = await Promise.all([
        this.store.request(getUserItems({ page: '1' })),
        this.store.request(getTags()),
      ]);

      const itemsJson = itemsResult.content as {
        data?: UserItem[];
      };
      const tagsJson = tagsResult.content as {
        data?: Tag[];
      };

      // Find the user-item matching this catalog item ID
      const items = itemsJson.data ?? [];
      this.userItem =
        items.find((ui) => {
          const embedded = ui.item as Record<string, unknown> | null;
          return embedded?.['id'] === this.args.itemId;
        }) ?? null;

      this.tags = tagsJson.data ?? [];
    } catch {
      // Silently fail — actions just won't show
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async markDone() {
    if (!this.userItem) return;
    this.actionInProgress = true;
    try {
      await this.store.request(
        updateUserItemStatus(this.userItem.id!, 'watched')
      );
      this.notifications.success('Marked as done');
      void this.loadUserItem();
    } catch {
      this.notifications.error('Failed to update');
    } finally {
      this.actionInProgress = false;
    }
  }

  @action
  async dismiss() {
    if (!this.userItem) return;
    this.actionInProgress = true;
    try {
      await this.store.request(
        updateUserItemStatus(this.userItem.id!, 'dismissed')
      );
      this.notifications.success('Item dismissed');
      void this.loadUserItem();
    } catch {
      this.notifications.error('Failed to dismiss');
    } finally {
      this.actionInProgress = false;
    }
  }

  @action
  async restore() {
    if (!this.userItem) return;
    this.actionInProgress = true;
    try {
      await this.store.request(
        updateUserItemStatus(this.userItem.id!, 'active')
      );
      this.notifications.success('Item restored');
      void this.loadUserItem();
    } catch {
      this.notifications.error('Failed to restore');
    } finally {
      this.actionInProgress = false;
    }
  }

  <template>
    {{#if this.session.isAuthenticated}}
      {{#if this.isLoading}}
        <span class="loading loading-spinner loading-xs"></span>
      {{else if this.userItem}}
        <div class="flex flex-wrap items-center gap-2">
          {{#if (eq this.userItem.status "watched")}}
            <span class="badge badge-success gap-1">
              <Icon @icon={{CheckIcon}} class="h-3 w-3" />
              Done
            </span>
            <button
              type="button"
              class="btn btn-xs btn-ghost"
              title="Restore to active"
              disabled={{this.actionInProgress}}
              {{on "click" this.restore}}
            >
              <Icon @icon={{UndoIcon}} class="h-3 w-3" />
              Undo
            </button>
          {{else if (eq this.userItem.status "dismissed")}}
            <span class="badge badge-ghost gap-1">Dismissed</span>
            <button
              type="button"
              class="btn btn-xs btn-ghost"
              title="Restore"
              disabled={{this.actionInProgress}}
              {{on "click" this.restore}}
            >
              <Icon @icon={{UndoIcon}} class="h-3 w-3" />
              Restore
            </button>
          {{else}}
            <button
              type="button"
              class={{tw "btn btn-sm btn-success btn-outline gap-1"}}
              disabled={{this.actionInProgress}}
              {{on "click" this.markDone}}
            >
              <Icon @icon={{CheckIcon}} class="h-4 w-4" />
              Done
            </button>
            <button
              type="button"
              class="btn btn-sm btn-ghost gap-1"
              title="Dismiss"
              disabled={{this.actionInProgress}}
              {{on "click" this.dismiss}}
            >
              <Icon @icon={{XIcon}} class="h-4 w-4" />
              Dismiss
            </button>
          {{/if}}

          <TagPicker
            @userItemId={{this.userItem.id}}
            @tags={{this.tags}}
            @onTagsChanged={{this.loadUserItem}}
          />
        </div>
      {{/if}}
    {{/if}}
  </template>
}
